import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  HelpOutline,
  Visibility,
  AssignmentInd,
  Reply,
  CheckCircle,
  InfoOutlined,
  Schedule,
  Done,
  Close,
  Inbox,
} from '@mui/icons-material';

// ─── Design tokens ─────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── Types ─────────────────────────────────────────────────────────
interface AttachmentItem {
  id: number;
  storageKey: string;
  mimeType: string;
  fileSize: number;
  downloadUrl: string;
}

interface InquiryAdminListItem {
  id: number;
  userId: number;
  categoryKey: string;
  categoryTitle: string;
  title: string;
  statusKey: string;
  statusTitle: string;
  adminId?: number;
  createdTime: string;
}

interface AdminInquiryComment {
  id: number;
  authorType: 'USER' | 'ADMIN';
  content: string;
  createdAt: string;
}

interface InquiryAdminDetail extends InquiryAdminListItem {
  content: string;
  comments: AdminInquiryComment[];
  closedAt?: string;
  updatedTime?: string;
}

interface PageResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

// ─── Helpers ───────────────────────────────────────────────────────
const formatDateTime = (s: string) =>
  new Date(s).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const formatRelative = (s: string) => {
  const diff = Date.now() - new Date(s).getTime();
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (d > 0) return `${d}일 전`;
  if (h > 0) return `${h}시간 전`;
  return '방금 전';
};

// ─── Status config ─────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string; icon: React.ReactNode }> = {
  SUBMITTED:   { label: '접수됨',   color: '#E65100', bg: '#FFF3E0', dot: '#FF6D00', icon: <Inbox sx={{ fontSize: 13 }} /> },
  IN_PROGRESS: { label: '처리중',   color: '#1565C0', bg: '#E3F2FD', dot: '#1976D2', icon: <Schedule sx={{ fontSize: 13 }} /> },
  REPLIED:     { label: '답변완료', color: '#2E7D32', bg: '#E8F5E9', dot: '#43A047', icon: <Done sx={{ fontSize: 13 }} /> },
  CLOSED:      { label: '종결됨',   color: '#37474F', bg: '#ECEFF1', dot: '#607D8B', icon: <Close sx={{ fontSize: 13 }} /> },
  REOPENED:    { label: '재개됨',   color: '#6A1B9A', bg: '#F3E5F5', dot: '#AB47BC', icon: <HelpOutline sx={{ fontSize: 13 }} /> },
};

const CATEGORY_CONFIG: Record<string, string> = {
  ACCOUNT:   '#1976D2',
  PAYMENT:   '#E65100',
  PRIVACY:   '#6A1B9A',
  TECHNICAL: '#00838F',
  REPORT:    '#D32F2F',
  OTHER:     '#616161',
};

// ─── Sub-component: StatCard ────────────────────────────────────────
interface StatCardProps { icon: React.ReactNode; value: number; label: string; accent: string; pulse?: boolean; }
const StatCard: React.FC<StatCardProps> = ({ icon, value, label, accent, pulse }) => (
  <Card elevation={0} sx={{
    border: '1px solid #F0E8F5', borderLeft: `4px solid ${accent}`, borderRadius: '14px',
    p: 2.5, display: 'flex', alignItems: 'center', gap: 2,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
  }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '12px', background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent, flexShrink: 0, position: 'relative' }}>
      {icon}
      {pulse && value > 0 && <Box sx={{ position: 'absolute', top: -4, right: -4, width: 12, height: 12, borderRadius: '50%', background: accent, animation: 'pulse 1.5s ease-in-out infinite' }} />}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: accent, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 500, mt: 0.25 }}>{label}</Typography>
    </Box>
  </Card>
);

// ─── Main component ─────────────────────────────────────────────────
const AdminInquiriesPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [inquiries, setInquiries]           = useState<InquiryAdminListItem[]>([]);
  const [selectedInquiry, setSelected]      = useState<InquiryAdminDetail | null>(null);
  const [detailDialog, setDetailDialog]     = useState(false);
  const [replyDialog, setReplyDialog]       = useState(false);
  const [replyContent, setReplyContent]     = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [page, setPage]                     = useState(0);
  const [totalPages, setTotalPages]         = useState(0);
  const [totalElements, setTotalElements]   = useState(0);
  const [loading, setLoading]               = useState(false);
  const [detailLoading, setDetailLoading]   = useState(false);
  const [actionLoading, setActionLoading]   = useState(false);
  const [attachments, setAttachments]       = useState<AttachmentItem[]>([]);
  const [snack, setSnack]                   = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });

  const adminId = Number((user as any)?.userId ?? user?.id) || 0;

  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  const showSnack = (msg: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, msg, severity });

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), size: '20' });
      if (filterStatus)   qs.append('status', filterStatus);
      if (filterCategory) qs.append('category', filterCategory);
      const res = await apiClient.get<{ resultType: string; result: PageResponse<InquiryAdminListItem> }>(
        `${API_ENDPOINTS.ADMIN.INQUIRIES.LIST}?${qs.toString()}`
      );
      setInquiries(res.result.content);
      setTotalPages(res.result.totalPages);
      setTotalElements(res.result.totalElements);
    } catch {
      showSnack('목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterCategory]);

  useEffect(() => {
    if (!isLoading && user?.role === 'ADMIN') fetchInquiries();
  }, [fetchInquiries, isLoading, user]);

  const openDetail = async (item: InquiryAdminListItem) => {
    setDetailLoading(true);
    setDetailDialog(true);
    setAttachments([]);
    try {
      const res = await apiClient.get<{ resultType: string; result: InquiryAdminDetail }>(
        API_ENDPOINTS.ADMIN.INQUIRIES.DETAIL(item.id)
      );
      setSelected(res.result);
      try {
        const attRes = await apiClient.get<{ resultType: string; result: { attachments: AttachmentItem[] } }>(
          `/api/v1/gth-common/silver-connect/inquiries/${item.id}/attachments`
        );
        setAttachments(attRes.result.attachments);
      } catch {
        // attachment fetch failure does not block detail display
      }
    } catch {
      showSnack('상세 조회에 실패했습니다.', 'error');
      setDetailDialog(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAssign = async (id: number) => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.INQUIRIES.ASSIGN(id), { adminId });
      showSnack('배정되었습니다.');
      fetchInquiries();
    } catch {
      showSnack('배정에 실패했습니다.', 'error');
    }
  };

  const handleReply = async () => {
    if (!selectedInquiry || !replyContent.trim()) return;
    setActionLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.INQUIRIES.REPLY(selectedInquiry.id), { adminId, replyContent });
      showSnack('답변이 등록되었습니다.');
      setReplyDialog(false);
      setReplyContent('');
      fetchInquiries();
    } catch {
      showSnack('답변 등록에 실패했습니다.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async (id: number) => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.INQUIRIES.CLOSE(id));
      showSnack('문의가 종결되었습니다.');
      setDetailDialog(false);
      fetchInquiries();
    } catch {
      showSnack('종결 처리에 실패했습니다.', 'error');
    }
  };

  const stats = {
    total:       totalElements,
    submitted:   inquiries.filter(i => i.statusKey === 'SUBMITTED').length,
    inProgress:  inquiries.filter(i => i.statusKey === 'IN_PROGRESS').length,
    replied:     inquiries.filter(i => i.statusKey === 'REPLIED').length,
  };

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  return (
    <AdminLayout>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(1.3)} }
        @keyframes rowIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
      `}</style>

      <Box sx={{ p: { xs: 2, md: 4 }, background: '#F8F4F9', minHeight: '100vh' }}>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ color: PINK, fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem' }}>
            ADMIN · SUPPORT
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={{
              fontWeight: 900, mt: 0.5,
              background: `linear-gradient(135deg, #1A0A2E 30%, ${CORAL})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              문의 관리
            </Typography>
            {stats.submitted > 0 && (
              <Chip label={`미처리 ${stats.submitted}건`} size="small"
                sx={{ background: '#FF6D00', color: '#fff', fontWeight: 700, fontSize: '0.7rem', animation: 'pulse 2s ease-in-out infinite' }} />
            )}
          </Box>
          <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 0.5 }}>
            사용자 문의 접수 현황 및 답변 관리
          </Typography>
        </Box>

        {/* Stat cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
          <StatCard icon={<HelpOutline sx={{ fontSize: 24 }} />} value={stats.total}      label="전체 문의"  accent="#7B1FA2" />
          <StatCard icon={<Inbox sx={{ fontSize: 24 }} />}       value={stats.submitted}  label="접수 대기"  accent="#E65100" pulse />
          <StatCard icon={<Schedule sx={{ fontSize: 24 }} />}    value={stats.inProgress} label="처리중"     accent="#1565C0" />
          <StatCard icon={<CheckCircle sx={{ fontSize: 24 }} />} value={stats.replied}    label="답변 완료"  accent="#2E7D32" />
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>상태</InputLabel>
            <Select value={filterStatus} label="상태" onChange={e => { setFilterStatus(e.target.value); setPage(0); }} sx={{ borderRadius: '10px' }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="SUBMITTED">접수됨</MenuItem>
              <MenuItem value="IN_PROGRESS">처리중</MenuItem>
              <MenuItem value="REPLIED">답변완료</MenuItem>
              <MenuItem value="CLOSED">종결됨</MenuItem>
              <MenuItem value="REOPENED">재개됨</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>카테고리</InputLabel>
            <Select value={filterCategory} label="카테고리" onChange={e => { setFilterCategory(e.target.value); setPage(0); }} sx={{ borderRadius: '10px' }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="ACCOUNT">계정</MenuItem>
              <MenuItem value="PAYMENT">결제</MenuItem>
              <MenuItem value="PRIVACY">개인정보</MenuItem>
              <MenuItem value="TECHNICAL">기술지원</MenuItem>
              <MenuItem value="REPORT">신고하기</MenuItem>
              <MenuItem value="OTHER">기타</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #F0E8F5', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: PINK }} /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(90deg, #1A0A2E 0%, #4A1265 100%)' }}>
                    {['ID', '카테고리', '제목', '접수 시각', '상태', '담당자', '처리'].map(h => (
                      <TableCell key={h} sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: '0.75rem', py: 1.8, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inquiries.map((item, idx) => {
                    const cfg = STATUS_CONFIG[item.statusKey] ?? STATUS_CONFIG['SUBMITTED'];
                    const isPending = item.statusKey === 'SUBMITTED';
                    const catColor = CATEGORY_CONFIG[item.categoryKey] ?? '#555';
                    return (
                      <TableRow key={item.id} onClick={() => openDetail(item)} sx={{
                        animation: `rowIn 0.3s ease ${idx * 0.04}s both`,
                        borderLeft: isPending ? '4px solid #FF6D00' : '4px solid transparent',
                        background: isPending ? '#FFF8F0' : '#fff',
                        '&:hover': { background: isPending ? '#FFF0E0' : '#FAF5FF' },
                        '& td': { borderBottom: '1px solid #F3EBF8' },
                        cursor: 'pointer',
                      }}>
                        <TableCell><Typography sx={{ fontWeight: 700, color: '#555', fontSize: '0.85rem' }}>#{item.id}</Typography></TableCell>
                        <TableCell>
                          <Chip label={item.categoryTitle} size="small"
                            sx={{ background: `${catColor}15`, color: catColor, border: `1px solid ${catColor}30`, fontWeight: 700, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#888', whiteSpace: 'nowrap' }}>{formatRelative(item.createdTime)}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#BBB' }}>{formatDateTime(item.createdTime)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33`, px: 1.2, py: 0.4, borderRadius: '100px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, ...(isPending && { animation: 'pulse 1.5s ease-in-out infinite' }) }} />
                            {cfg.label}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: item.adminId ? '#333' : '#BBB' }}>
                            {item.adminId ? `#${item.adminId}` : '미배정'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="상세 보기">
                              <IconButton size="small" onClick={() => openDetail(item)} sx={{ color: '#9C27B0', '&:hover': { background: '#F3E5F5' } }}>
                                <Visibility sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {!item.adminId && (
                              <Tooltip title="배정">
                                <IconButton size="small" onClick={() => handleAssign(item.id)} sx={{ color: '#1976D2', '&:hover': { background: '#E3F2FD' } }}>
                                  <AssignmentInd sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(item.statusKey === 'SUBMITTED' || item.statusKey === 'IN_PROGRESS' || item.statusKey === 'REOPENED') && (
                              <Tooltip title="답변">
                                <IconButton size="small" onClick={() => { openDetail(item).then(() => { setReplyDialog(true); }); }} sx={{ color: '#2E7D32', '&:hover': { background: '#E8F5E9' } }}>
                                  <Reply sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {inquiries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <InfoOutlined sx={{ fontSize: 40, color: '#DDD', mb: 1 }} />
                        <Typography sx={{ color: '#AAA' }}>문의 내역이 없습니다.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination count={totalPages} page={page + 1} onChange={(_, v) => setPage(v - 1)} color="primary" sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px' } }} />
          </Box>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: PINK }} /></Box>
          ) : selectedInquiry && (() => {
            const cfg = STATUS_CONFIG[selectedInquiry.statusKey] ?? STATUS_CONFIG['SUBMITTED'];
            const catColor = CATEGORY_CONFIG[selectedInquiry.categoryKey] ?? '#555';
            return (
              <>
                <DialogTitle sx={{ p: 0 }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #1A0A2E 0%, #4A1265 100%)', p: 3, color: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: `${catColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                        <HelpOutline />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>문의 #{selectedInquiry.id}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{formatDateTime(selectedInquiry.createdTime)} 접수</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#E3F2FD08', border: '1px solid #1976D220' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>사용자 ID</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1976D2' }}>#{selectedInquiry.userId}</Typography>
                      </Box>
                      <Box sx={{ p: 2, borderRadius: '12px', background: `${catColor}08`, border: `1px solid ${catColor}20` }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>카테고리</Typography>
                        <Chip label={selectedInquiry.categoryTitle} size="small"
                          sx={{ background: `${catColor}15`, color: catColor, fontWeight: 700 }} />
                      </Box>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>제목</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#1A0A2E' }}>{selectedInquiry.title}</Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 1 }}>문의 내용</Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>{selectedInquiry.content}</Typography>
                    </Box>
                    {selectedInquiry.comments && selectedInquiry.comments.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600 }}>대화 내역</Typography>
                        {selectedInquiry.comments.map((comment) => (
                          <Box
                            key={comment.id}
                            sx={{
                              p: 2, borderRadius: '12px',
                              background: comment.authorType === 'ADMIN' ? '#E8F5E9' : '#F8F4F9',
                              border: `1px solid ${comment.authorType === 'ADMIN' ? '#A5D6A7' : '#EDE0F5'}`,
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: comment.authorType === 'ADMIN' ? '#2E7D32' : '#7B1FA2' }}>
                                {comment.authorType === 'ADMIN' ? '상담사 답변' : '사용자 추가 문의'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>{formatDateTime(comment.createdAt)}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>{comment.content}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {attachments.length > 0 && (
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 1 }}>
                          첨부파일 ({attachments.length}개)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {attachments.map((att) => (
                            <Box
                              key={att.id}
                              component="a"
                              href={att.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                width: 80, height: 80, borderRadius: '8px', overflow: 'hidden',
                                border: '1px solid #E0E0E0', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                textDecoration: 'none',
                                '&:hover': { borderColor: '#9C27B0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                              }}
                            >
                              {att.mimeType.startsWith('image/') ? (
                                <img src={att.downloadUrl} alt="첨부파일" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Box sx={{ textAlign: 'center', p: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.6rem', color: '#666', wordBreak: 'break-all' }}>
                                    {att.mimeType === 'application/pdf' ? 'PDF' : 'FILE'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.55rem', color: '#999' }}>
                                    {(att.fileSize / 1024).toFixed(0)}KB
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ p: 2, borderRadius: '12px', background: `${cfg.color}08`, border: `1px solid ${cfg.color}25`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600 }}>처리 상태</Typography>
                        <Typography sx={{ fontWeight: 700, color: cfg.color, fontSize: '0.9rem' }}>{cfg.label}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                  {(selectedInquiry.statusKey === 'SUBMITTED' || selectedInquiry.statusKey === 'IN_PROGRESS' || selectedInquiry.statusKey === 'REOPENED') && (
                    <Button variant="outlined" onClick={() => setReplyDialog(true)}
                      sx={{ borderRadius: '10px', borderColor: '#2E7D32', color: '#2E7D32', textTransform: 'none', fontWeight: 700 }}
                      startIcon={<Reply sx={{ fontSize: 16 }} />}>
                      답변
                    </Button>
                  )}
                  {selectedInquiry.statusKey === 'REPLIED' && (
                    <Button variant="outlined" onClick={() => handleClose(selectedInquiry.id)}
                      sx={{ borderRadius: '10px', borderColor: '#37474F', color: '#37474F', textTransform: 'none', fontWeight: 700 }}
                      startIcon={<CheckCircle sx={{ fontSize: 16 }} />}>
                      종결
                    </Button>
                  )}
                  <Button onClick={() => setDetailDialog(false)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>닫기</Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* Reply Dialog */}
        <Dialog open={replyDialog} onClose={() => setReplyDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ p: 0 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #1B5E20, #2E7D32)', p: 3, color: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Reply />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>답변 작성</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)' }}>
                    문의 #{selectedInquiry?.id} · {selectedInquiry?.title}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3, pt: 3 }}>
            {selectedInquiry?.content && (
              <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5', mb: 2.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>원본 문의</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.6 }}>{selectedInquiry.content}</Typography>
              </Box>
            )}
            <TextField
              fullWidth multiline rows={5}
              label="답변 내용"
              value={replyContent}
              onChange={e => setReplyContent(e.target.value)}
              placeholder="사용자에게 전달할 답변을 작성해주세요..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', '&.Mui-focused fieldset': { borderColor: '#2E7D32' } }, '& label.Mui-focused': { color: '#2E7D32' } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button onClick={() => { setReplyDialog(false); setReplyContent(''); }} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>취소</Button>
            <Button variant="contained" disabled={actionLoading || !replyContent.trim()} onClick={handleReply}
              sx={{ borderRadius: '10px', background: '#2E7D32', textTransform: 'none', fontWeight: 700, px: 3, '&:hover': { background: '#1B5E20' } }}
              startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Reply />}>
              답변 등록
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default AdminInquiriesPage;
